import { Button, clx, Heading } from '@medusajs/ui';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { getApiErrorMessage } from '@lib/util/get-api-error-message';
import { useResetPassword } from '@lib/query/mutations/auth';
import { FormLabel } from '@modules/common/components/form-field';
import Input from '@modules/common/components/input';
import Modal from '@modules/common/components/modal';

type ForgetPasswordModalProps = {
  modalState: boolean;
  closeModal: () => void;
  dataTestId: string;
  title: string;
  confirmText?: string;
};

export const ForgetPasswordModal: React.FC<ForgetPasswordModalProps> = ({
  modalState,
  closeModal,
  dataTestId,
  title,
  confirmText = '確認',
}) => {
  const [email, setEmail] = React.useState('');
  const [errors, setErrors] = React.useState<{ email: string }>({ email: '' });
  const navigate = useNavigate();
  const { countryCode } = useParams<{ countryCode: string }>();

  const resetPassword = useResetPassword({
    onSuccess: () => {
      setErrors({ email: '' });
      navigate(`/${countryCode ?? 'tw'}/account/forget-password-sent`);
    },
    onError: (error) => {
      setErrors({
        email: getApiErrorMessage(error, '密碼重設失敗，請稍後再試'),
      });
    },
  });

  const handleSubmit = async () => {
    const pattern = /^[\w-.+]+@([\w-]+\.)+[\w-]{2,4}$/;
    if (!pattern.test(email)) {
      setErrors({ email: '您輸入的Email格式有誤' });
      return;
    }
    resetPassword.mutate({ email });
  };

  return (
    <Modal
      isOpen={modalState}
      close={closeModal}
      data-testid={dataTestId}
      panelClassName="max-h-[90vh] max-w-[469px] border-0 bg-white-10"
    >
      <Modal.Title>
        <Heading className="pb-4 text-xl font-bold">{title}</Heading>
      </Modal.Title>
      <div className="flex flex-col gap-y-4">
        <span className="text-black-10 max-w-[388px] text-sm">
          請輸入您的帳號或您註冊用的 Email 。<br />
          我們會發送一封 Email 給您，Email 裡面附有重新設定密碼的連結。
        </span>
        <div>
          <FormLabel className="txt-large">電子信箱</FormLabel>
          <Input
            className={clx(
              'bg-white-10 hover:border-blue-30 focus:border-blue-30 active:border-blue-30 h-12 w-full rounded-md border px-2 py-2',
              {
                'border-red-60': errors.email,
              }
            )}
            name="email"
            autoComplete="email"
            placeholder="請輸入您的Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="email-input"
          />
          {errors.email && (
            <span className="text-red-60 text-sm">{errors.email}</span>
          )}
        </div>
        <Modal.Footer>
          <div className="flex gap-4">
            <Button
              type="reset"
              variant="secondary"
              onClick={closeModal}
              className="border-black-10 text-black-10 rounded-sm border border-solid px-4 py-2 text-base"
              data-testid="cancel-button"
            >
              取消
            </Button>
            <Button
              className="text-white-10 disabled:bg-white-40 disabled:text-white-10 rounded-sm bg-red-50 px-4 py-2 shadow-none disabled:text-base disabled:font-bold disabled:shadow-none"
              data-testid="save-button"
              onClick={handleSubmit}
              disabled={!email || resetPassword.isPending}
              isLoading={resetPassword.isPending}
            >
              {confirmText}
            </Button>
          </div>
        </Modal.Footer>
      </div>
    </Modal>
  );
};
